/**
 * Binding Form Modal Component
 * Modal for adding a new binding to a user
 */

import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  MenuItem,
  Box,
  Typography,
  CircularProgress,
  Alert,
  AlertTitle,
} from '@mui/material';

import {
  BindingFormData,
  BindingEntityType,
  BindingFunction,
  BindingStatus,
  BINDING_FUNCTION_LABELS,
  UserBinding,
  getBindingFunctionLabel,
} from '../../../types/user';
import { useBindingScope } from '../../../hooks/useBindingScope';
import BindingEntityField, { BindingEntityValue } from './BindingEntityField';

const BINDING_FUNCTION_OPTIONS = Object.values(BindingFunction).map((fn) => ({
  value: fn,
  label: BINDING_FUNCTION_LABELS[fn],
}));

const ROTULO_DA_FUNCAO: Record<BindingEntityType, string> = {
  [BindingEntityType.COMPANY]: 'Função na empresa',
  [BindingEntityType.PROVIDER]: 'Função na clínica',
};

const ENTIDADE_VAZIA: BindingEntityValue = { entityType: '', entityId: '' };

interface BindingFormModalProps {
  open: boolean;
  onClose: () => void;
  onSubmit: (data: BindingFormData) => Promise<void>;
  loading?: boolean;
  error?: string | null;
  /** Quem está sendo vinculado — é dele que o modal fala. */
  userName: string;
  /** Vínculos já existentes: o servidor recusa um segundo para a mesma entidade. */
  existingBindings: UserBinding[];
}

const BindingFormModal: React.FC<BindingFormModalProps> = ({
  open,
  onClose,
  onSubmit,
  loading = false,
  error,
  userName,
  existingBindings,
}) => {
  const { entidadeFixa, podeTrocarDeContexto } = useBindingScope();
  const [entidade, setEntidade] = useState<BindingEntityValue>(ENTIDADE_VAZIA);
  const [funcao, setFuncao] = useState<BindingFunction>(BindingFunction.EMPLOYEE);

  // Fora da plataforma a entidade não é escolha, é consequência do contexto: derivar em vez de
  // guardar evita o estado que discorda do contexto ativo depois de uma troca.
  const entidadeEscolhida: BindingEntityValue = entidadeFixa
    ? { entityType: entidadeFixa.entityType, entityId: String(entidadeFixa.entityId) }
    : entidade;

  const tipoEmFoco = entidadeEscolhida.entityType || BindingEntityType.COMPANY;

  const vinculoExistente = existingBindings.find(
    (binding) =>
      binding.status === BindingStatus.ACTIVE &&
      binding.entityType === entidadeEscolhida.entityType &&
      String(binding.entityId) === entidadeEscolhida.entityId
  );

  const limpar = () => {
    setEntidade(ENTIDADE_VAZIA);
    setFuncao(BindingFunction.EMPLOYEE);
  };

  const handleClose = () => {
    if (loading) return;
    limpar();
    onClose();
  };

  const handleSubmit = async (evento: React.FormEvent) => {
    evento.preventDefault();
    if (!entidadeEscolhida.entityType || !entidadeEscolhida.entityId || vinculoExistente) return;
    await onSubmit({
      entityType: entidadeEscolhida.entityType,
      entityId: entidadeEscolhida.entityId,
      function: funcao,
    });
    limpar();
  };

  // Com a entidade determinada pelo contexto não há outra a escolher: o vínculo que já existe
  // encerra o assunto, e oferecer um formulário só levaria à recusa do servidor.
  const semNadaAFazer = Boolean(entidadeFixa && vinculoExistente);

  const saidaDoBloqueio = podeTrocarDeContexto
    ? 'Para vincular a outra, troque no seletor do topo da tela.'
    : 'Para vincular a outra, peça a quem administra os acessos.';

  const nomeDaEntidade =
    entidadeFixa?.nome ||
    (tipoEmFoco === BindingEntityType.COMPANY ? 'esta empresa' : 'esta clínica');

  if (semNadaAFazer) {
    return (
      <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
        <DialogTitle>Vincular {userName}</DialogTitle>
        <DialogContent>
          <Alert severity="info" icon={false}>
            <AlertTitle>{userName} já faz parte de {nomeDaEntidade}</AlertTitle>
            {vinculoExistente && (
              <>
                Está vinculada como{' '}
                <strong>{getBindingFunctionLabel(vinculoExistente.function)}</strong> desde{' '}
                {new Date(vinculoExistente.startedAt).toLocaleDateString('pt-BR')}. Uma pessoa tem
                um vínculo por entidade.
              </>
            )}
          </Alert>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
            Para mudar a função, remova este vínculo e crie outro. {saidaDoBloqueio}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose} variant="outlined">
            Entendi
          </Button>
        </DialogActions>
      </Dialog>
    );
  }

  const podeGravar =
    Boolean(entidadeEscolhida.entityType && entidadeEscolhida.entityId) && !vinculoExistente;

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ pb: 1 }}>
        Vincular {userName}
        <Typography variant="body2" color="text.secondary">
          {entidadeFixa
            ? 'O vínculo passa a valer imediatamente.'
            : 'Você está na plataforma: escolha a quem esta pessoa pertence.'}
        </Typography>
      </DialogTitle>
      <form onSubmit={handleSubmit}>
        <DialogContent>
          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}

          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5, pt: 1 }}>
            <BindingEntityField
              value={entidadeEscolhida}
              onChange={setEntidade}
              entidadeFixa={entidadeFixa}
              disabled={loading}
            />

            {vinculoExistente && (
              <Alert severity="info">
                {userName} já está vinculada aqui como{' '}
                <strong>{getBindingFunctionLabel(vinculoExistente.function)}</strong>. Escolha outra
                entidade.
              </Alert>
            )}

            <TextField
              select
              fullWidth
              label={ROTULO_DA_FUNCAO[tipoEmFoco]}
              value={funcao}
              onChange={(evento) => setFuncao(evento.target.value as BindingFunction)}
              disabled={loading}
              helperText="O que a pessoa faz aqui. Pode ser alterado depois."
            >
              {BINDING_FUNCTION_OPTIONS.map((opcao) => (
                <MenuItem key={opcao.value} value={opcao.value}>
                  {opcao.label}
                </MenuItem>
              ))}
            </TextField>

            {entidadeFixa && (
              <Typography variant="caption" color="text.secondary" sx={{ borderTop: 1, borderColor: 'divider', pt: 1.5 }}>
                O vínculo diz que a pessoa é da sua equipe, então ele só vale para{' '}
                {entidadeFixa.entityType === BindingEntityType.COMPANY
                  ? 'a empresa'
                  : 'a clínica'}{' '}
                em que você está operando agora. {saidaDoBloqueio}
              </Typography>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose} disabled={loading}>
            Cancelar
          </Button>
          <Button type="submit" variant="contained" disabled={loading || !podeGravar}>
            {loading ? <CircularProgress size={24} /> : 'Vincular'}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
};

export default BindingFormModal;
